import { FormDatePicker, FormFieldGroup, FormInput, FormLabel, FormRadioGroup, FormSelect, FormImageUpload } from '$components'
import { z } from 'zod'
import { Input } from './types'
import { useInfiniteClientOptions } from '$hooks/use-infinite-client-options'
import { DogBreed, Gender } from '$api-client/types.gen'
import { useState } from 'react'
import { useCreatePetForm } from '../create'
import { useInfiniteSizeCategoryOptions } from '$hooks/use-infinite-size-category-options'
import { useInfiniteCoatTypeOptions } from '$hooks/use-infinite-coat-type-options'
import { PetOptionSettingsLink } from '$components'

export const detailsSchemaBase = z.object({
  [Input.Image]: z.instanceof(File).optional(),
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Parent]: z.object({
    id: z.number(),
    value: z.string(),
  }, {
    required_error: 'Parent is required.',
  }),
  [Input.Breed]: z.object({
    id: z.string(),
    value: z.string(),
  }, {
    required_error: 'Breed is required.',
  }),
  [Input.SizeCategory]: z.object({
    id: z.string(),
    value: z.string(),
  }).optional(),
  [Input.CoatType]: z.object({
    id: z.string(),
    value: z.string(),
  }).optional(),
  [Input.DateOfBirth]: z.string().optional(),
  [Input.Gender]: z.string({
    required_error: 'Gender is required.',
  }),
})

export const detailsSchema = detailsSchemaBase

export function DetailsStep() {
  const [breedSearch, setBreedSearch] = useState<string>('')
  const { options, search, async } = useInfiniteClientOptions()
  const { fileHandler } = useCreatePetForm()
  const { options: sizeOptions, search: sizeSearch, async: sizeAsync } = useInfiniteSizeCategoryOptions()
  const { options: coatOptions, search: coatSearch, async: coatAsync } = useInfiniteCoatTypeOptions()

  return (
    <div className="flex flex-col gap-stack-section flex-grow">
      <div className="grid md:grid-cols-[auto_1fr] gap-stack-grid">
        <FormFieldGroup>
          <FormLabel htmlFor={Input.Image}>
            Profile Image
          </FormLabel>

          <FormImageUpload
            name={Input.Image}
            disabled={fileHandler.isUploading(Input.Image)}
            isLoading={fileHandler.isUploading(Input.Image)}
            onChange={(file) => fileHandler.uploadFile(Input.Image, file)}
          />
        </FormFieldGroup>

        <div className="flex flex-col gap-stack-section">
          <FormFieldGroup>
            <FormLabel htmlFor={Input.Name}>
              Name
            </FormLabel>

            <FormInput
              name={Input.Name}
              placeholder="Enter pet name..."
            />
          </FormFieldGroup>

          <FormFieldGroup>
            <FormLabel htmlFor={Input.Parent}>
              Parent
            </FormLabel>

            <FormSelect
              name={Input.Parent}
              placeholder="Select parent..."
              search={search}
              async={async}
              options={options}
            />
          </FormFieldGroup>
        </div>
      </div>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Breed}>
          Breed
        </FormLabel>

        <FormSelect
          name={Input.Breed}
          placeholder="Select breed..."
          clearable
          search={{
            search: breedSearch,
            onSearchChange: setBreedSearch,
          }}
          options={Object.entries(DogBreed).map(([id, value]) => ({ id, value }))}
        />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.SizeCategory}>
          Size Category
        </FormLabel>

        <FormSelect
          name={Input.SizeCategory}
          placeholder="Select size..."
          options={sizeOptions}
          search={sizeSearch}
          async={sizeAsync}
        />

        <PetOptionSettingsLink type="size" />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.CoatType}>
          Coat Type
        </FormLabel>

        <FormSelect
          name={Input.CoatType}
          placeholder="Select coat type..."
          options={coatOptions}
          search={coatSearch}
          async={coatAsync}
        />

        <PetOptionSettingsLink type="coat" />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.DateOfBirth}>
          Date of Birth
        </FormLabel>

        <FormDatePicker name={Input.DateOfBirth} />
      </FormFieldGroup>

      <FormFieldGroup>
        <FormLabel htmlFor={Input.Gender}>
          Gender
        </FormLabel>

        <FormRadioGroup
          name={Input.Gender}
          options={Object.entries(Gender).map(([id, value]) => ({
            id,
            label: value,
            value,
          }))}
        />
      </FormFieldGroup>
    </div>
  )
}
